<template>
  <div class="communications-page">
    <!-- 🌐 Top Navigation Bar -->
    <nav class="top-nav">
      <div class="nav-container">
        <div class="nav-left">
          <a href="/setting/messaging_settings" class="nav-link"
            >Messaging Settings</a
          >
          <a href="/communication_logs" class="nav-link">Messages History</a>
          <a href="/sms_credits" class="nav-link">Top-up</a>
        </div>

        <div class="nav-right">
          <div class="sms-balance">
            <span class="sms-icon">💬</span>
            <span class="sms-text">SMS Balance:</span>
            <span class="sms-value">{{ smsBalance }}</span>
          </div>
        </div>
      </div>
    </nav>

    <!-- 📨 Main Section -->
    <div
      v-if="loading"
      class="content"
      style="
        display: flex;
        justify-content: center;
        align-items: center;
        height: 50vh;
      "
    >
      <loader />
    </div>
    <div v-if="!loading" class="content">
      <!-- Left Card -->
      <div class="card left">
        <h2 class="page-title">Communications Center</h2>

        <!-- Send Option -->
        <div class="form-group">
          <label>Send Option</label>
          <select v-model="sendMode">
            <option value="now">Send Immediately</option>
            <option value="schedule">Schedule for Later</option>
          </select>
        </div>

        <div class="form-group" v-if="sendMode === 'schedule'">
          <label>Schedule Date & Time</label>
          <input type="datetime-local" v-model="scheduleTime" />
        </div>

        <!-- Channels -->
        <!-- Replace this block in your template -->
        <div class="form-group form-row channels-row">
          <label for="channels" class="field-label">Channels</label>

          <div
            class="channel-options"
            id="channels"
            role="group"
            aria-label="Message channels"
          >
            <label class="checkbox-label">
              <input type="checkbox" v-model="channels.email" />
              <span class="checkbox-text">Email</span>
            </label>

            <label class="checkbox-label">
              <input type="checkbox" v-model="channels.sms" />
              <span class="checkbox-text">SMS</span>
            </label>
          </div>
        </div>

        <!-- Email Section -->
        <div v-if="channels.email" class="section">
          <h3>Email Details</h3>
          <div class="form-group">
            <label>Subject</label>
            <input
              type="text"
              v-model="email.subject"
              placeholder="Email subject"
            />
          </div>
        </div>

        <!-- Message Section -->
        <div class="section">
          <h3>Message</h3>
          <div class="form-group">
            <textarea
              v-model="sms.message"
              rows="3"
              maxlength="160"
              placeholder="Type your SMS content (max 160 characters)"
            ></textarea>
          </div>
        </div>

        <!-- <button class="btn btn-green">Send Messages</button> -->
        <!-- <button v-if="!channels.email && !channels.sms" class="btn btn-green" @click="submitMessages">
          Send Messages
        </button> -->
        <button
          class="btn btn-green"
          @click="submitMessages"
          :disabled="!channels.email && !channels.sms"
        >
          Send Messages
        </button>
      </div>

      <!-- Right Card: Recipients -->
      <div class="card right">
        <h3>Recipients</h3>

        <div class="form-group">
          <label>Recipient Mode</label>
          <select v-model="recipientMode">
            <option value="all">All Patients</option>
            <option value="selected">Selected Patients</option>
          </select>
        </div>

        <!-- Selected Recipients -->
        <div v-if="recipientMode === 'selected'">
          <div class="form-group">
            <label>Search & Add</label>
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Search patients by name, phone, or enter a new contact"
              @keydown.enter.prevent="handleEnterAdd"
            />
            <ul
              v-if="filteredPatients.length && searchQuery"
              class="search-results"
            >
              <li
                v-for="patient in filteredPatients"
                :key="patient.id"
                @click="addRecipient(patient)"
              >
                {{ patient.name }} — {{ patient.phone }} — {{ patient.email }}
              </li>
            </ul>
          </div>

          <div class="form-group">
            <label>Selected Recipients</label>
            <div v-if="selectedPatients.length" class="recipient-list">
              <div
                v-for="patient in selectedPatients"
                :key="patient.id"
                class="recipient-item"
              >
                <!-- <span>{{ patient.name }} ({{ patient.phone }})</span> -->
                <span>{{ displayRecipient(patient) }}</span>

                <button @click="removeRecipient(patient.id)">×</button>
              </div>
            </div>
            <p v-else class="placeholder-text">No recipients selected yet.</p>
          </div>
        </div>

        <!-- All Patients -->
        <div v-else>
          <p class="placeholder-text">
            Message will be sent to <strong>all registered patients.</strong>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import loader from "./special_clinics/dialysis/loader.vue";
import { generalApiRequest } from "../api/apiIndex";
import showNotification from "../notification/notify";
export default {
  name: "Communications",
  data() {
    return {
      loading: false,
      smsBalance: "-",
      recipientMode: "all",
      searchQuery: "",
      patients: [], // fetched dynamically from API
      selectedPatients: [],
      sendMode: "now",
      scheduleTime: "",
      channels: { email: false, sms: false },
      email: { subject: "", body: "" },
      sms: { message: "" },
      loadingPatients: false, // show spinner while fetching
    };
  },

  computed: {
    filteredPatients() {
      return this.patients;
    },
  },

  components: {
    loader,
  },

  watch: {
    searchQuery(newVal) {
      if (newVal.length >= 2) {
        this.fetchPatients();
      } else {
        this.patients = [];
      }
    },
  },

  methods: {
    // 🔍 Fetch patients from your backend API
    async fetchPatients() {
      if (!this.searchQuery.trim()) {
        this.patients = [];
        return;
      }

      this.loadingPatients = true;
      try {
        const url = "searchSpecificCompanyUsers";
        const body = { search_param: this.searchQuery };
        const response = await generalApiRequest(url, "GET", body, 1);
        // const response = await fetch(
        //   `/api/patients/search?q=${encodeURIComponent(this.searchQuery)}`
        // );
        if (!response) throw new Error("Network response was not ok");
        console.log(response);

        const data = response; //await response.json();
        this.patients = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("❌ Failed to fetch patients:", error);
        this.patients = [];
      } finally {
        this.loadingPatients = false;
      }
    },

    // ➕ Add recipient (from API results or manual entry)
    addRecipient(patient) {
      if (!this.selectedPatients.find((p) => p.id === patient.id)) {
        this.selectedPatients.push(patient);
      }
      this.searchQuery = "";
      this.patients = []; // clear dropdown
    },

    // ❌ Remove recipient
    removeRecipient(id) {
      this.selectedPatients = this.selectedPatients.filter((p) => p.id !== id);
    },

    // 🧾 Format how recipient is displayed
    displayRecipient(patient) {
      if (patient.name && patient.name !== "Manual Entry") {
        const contact = patient.phone + " — " + patient.email || "—";
        return `${patient.name} (${contact})`;
      }
      return patient.phone || patient.email || "Unknown";
    },

    // ⌨️ Handle Enter key for manual entries
    handleEnterAdd() {
      const value = this.searchQuery.trim();
      if (!value) return;

      // Validate input type
      const phonePattern = /^\+?\d{7,15}$/;
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (phonePattern.test(value) || emailPattern.test(value)) {
        // Avoid duplicates
        const alreadyExists = this.selectedPatients.some(
          (p) =>
            p.phone?.trim() === value.trim() ||
            (p.email && p.email.toLowerCase() === value.toLowerCase())
        );
        if (alreadyExists) {
          this.searchQuery = "";
          return;
        }

        // Create new manual entry recipient
        const newRecipient = {
          id: Date.now(),
          name: "Manual Entry",
          phone: phonePattern.test(value) ? value : "",
          email: emailPattern.test(value) ? value : "",
        };

        this.selectedPatients.push(newRecipient);
        this.searchQuery = "";
      } else {
        alert("Please enter a valid phone number or email address.");
      }
    },

    // 🚀 Submit all messages
    async submitMessages() {
      // ✅ Step 1: Validation
      if (!this.channels.email && !this.channels.sms) {
        alert("Please select at least one channel (Email or SMS).");
        return;
      }

      if (
        this.recipientMode === "selected" &&
        this.selectedPatients.length === 0
      ) {
        alert("Please select or add at least one recipient.");
        return;
      }

      // if (this.recipientMode === "all" && this.patients.length === 0) {
      //   alert("No registered patients found to send to.");
      //   return;
      // }

      if (this.sendMode === "schedule" && !this.scheduleTime) {
        alert("Please select a schedule date and time.");
        return;
      }

      if (this.channels.email && !this.email.subject.trim()) {
        alert("Please provide an email subject.");
        return;
      }

      if (this.channels.sms && !this.sms.message.trim()) {
        alert("Please enter your SMS message.");
        return;
      }

      this.loading = true;
      // ✅ Step 2: Prepare recipients
      const recipients =
        this.recipientMode === "all"
          ? this.patients.map((p) => ({
              name: p.name,
              phone: p.phone,
              email: p.email,
            }))
          : this.selectedPatients.map((p) => ({
              name: p.name,
              phone: p.phone,
              email: p.email,
            }));

      // ✅ Step 3: Create payload
      const payload = {
        recipientMode: this.recipientMode,
        send_mode: this.sendMode,
        schedule_time: this.scheduleTime || null,
        channels: Object.keys(this.channels).filter((c) => this.channels[c]),
        message: this.sms.message.trim(),
        email_subject: this.email.subject.trim(),
        recipients,
      };

      try {
        const url = "sendMessages";
        const body = { payload: payload };
        const response = await generalApiRequest(url, "POST", body, 1);

        if (!response) throw new Error("Message submission failed.");

        // alert("✅ Messages successfully queued!");
        await this.get_credit_balance();
        this.resetForm();
        showNotification("✅ Messages successfully queued!");

        this.loading = false;
      } catch (error) {
        console.error("❌ Failed to submit messages:", error);
        alert("Failed to send messages. Please try again.");
      }
      this.loading = false;
    },

    // 🧹 Reset form after successful submission
    resetForm() {
      this.sendMode = "now";
      this.scheduleTime = "";
      this.channels = { email: false, sms: false };
      this.email = { subject: "", body: "" };
      this.sms = { message: "" };
      this.selectedPatients = [];
      this.recipientMode = "all";
      this.searchQuery = "";
      this.patients = [];
    },

    async get_credit_balance() {
      const url = "get_available_credit";
      const body = {};
      const response = await generalApiRequest(url, "GET", body, 1);
      this.smsBalance = response.available_credit;
    },
  },
  mounted() {
    this.get_credit_balance();
  },
};
</script>

<style scoped>
/* 🔷 General Layout */
.communications-page {
  padding: 0;
  background: #f6f8fb;
  min-height: 100vh;
  font-family: "Inter", system-ui, sans-serif;
  color: #333;
}

/* 🔝 Professional Top Navigation */
.top-nav {
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 1000;
}

.nav-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 30px;
  max-width: 1200px;
  margin: 0 auto;
}

.nav-left {
  display: flex;
  gap: 25px;
}

.nav-link {
  color: #007bff;
  font-weight: 500;
  text-decoration: none;
  font-size: 15px;
  position: relative;
  transition: all 0.3s;
}

.nav-link:hover {
  color: #0056b3;
}

.nav-right {
  display: flex;
  align-items: center;
}

.sms-balance {
  background: #f3f9f3;
  padding: 6px 14px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d1e7dd;
}

.sms-icon {
  font-size: 16px;
}

.sms-text {
  font-weight: 500;
  color: #555;
}

.sms-value {
  font-weight: 700;
  color: #28a745;
}

/* 🧩 Page Content */
.content {
  display: flex;
  gap: 25px;
  max-width: 100%; /* 1200px */
  margin: 25px auto;
  flex-wrap: wrap;
}

/* 📦 Cards */
.card {
  background: #fff;
  border-radius: 10px;
  padding: 25px 30px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.05);
  flex: 1;
  min-width: 350px;
}

.page-title {
  text-align: center;
  font-size: 22px;
  color: #222;
  margin-bottom: 25px;
}

/* 🧱 Form Fields */
.form-group {
  margin-bottom: 16px;
}

label {
  font-weight: 600;
  display: block;
  margin-bottom: 6px;
  color: #444;
}

input,
select,
textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;
}

input:focus,
select:focus,
textarea:focus {
  border-color: #007bff;
  box-shadow: 0 0 4px rgba(0, 123, 255, 0.25);
}

/* 🔍 Search Results */
.search-results {
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  margin-top: 5px;
  list-style: none;
  padding: 0;
  max-height: 150px;
  overflow-y: auto;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.05);
}

.search-results li {
  padding: 8px 10px;
  cursor: pointer;
  transition: background 0.2s;
}

.search-results li:hover {
  background: #f1f7ff;
}

/* 🧍 Selected Recipients */
.recipient-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #eef5ff;
  padding: 6px 10px;
  border-radius: 6px;
  margin-bottom: 6px;
}

.recipient-item button {
  background: #e74c3c;
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  font-weight: bold;
  cursor: pointer;
  transition: 0.2s;
}

.recipient-item button:hover {
  background: #c0392b;
}

/* 🧾 Buttons */
.btn {
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  cursor: pointer;
  font-weight: 600;
  transition: 0.3s ease;
}

.btn-green {
  background: #28a745;
  color: white;
}

.btn-green:hover {
  background: #218838;
}

.placeholder-text {
  color: #777;
  font-size: 14px;
}
/* Add to your <style scoped> (keeps design consistent with the rest) */
/* .channels-row makes the label and control sit on one row */
.channels-row {
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap; /* allows responsive stacking on narrow screens */
}

/* make the main label a fixed-ish width for visual alignment */
.channels-row .field-label {
  min-width: 120px; /* adjust to taste */
  font-weight: 600;
  color: #444;
}

/* checkbox group aligns items center */
.channel-options {
  display: flex;
  gap: 16px;
  align-items: center;
}

/* each checkbox label becomes a tidy inline control */
.checkbox-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s;
}

/* subtle hover/focus affordances */
.checkbox-label:hover {
  background: rgba(0, 123, 255, 0.04);
}

/* style the actual native checkbox for consistent vertical alignment */
.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  margin: 0;
  display: inline-block;
  vertical-align: middle;
  accent-color: #007bff; /* modern tint for supported browsers */
}

/* text beside checkbox */
.checkbox-text {
  font-size: 14px;
  color: #333;
  line-height: 1;
}

/* responsive: if the container is too narrow, stack the controls under the label */
@media (max-width: 640px) {
  .channels-row {
    align-items: flex-start;
  }
  .channels-row .field-label {
    min-width: 0;
    width: 100%;
    margin-bottom: 6px;
  }
  .channel-options {
    width: 100%;
  }
}
</style>
